import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg, fmtDT } from "../lib/api";
import { PageHeader, StatusBadge, useLoad, Empty } from "../components/common";
import RemoveBySource from "../components/RemoveBySource";
import PoolWash from "../components/PoolWash";

const FIELDS = ["business_name", "contact_name", "first_name", "last_name", "phone", "email", "lead_date", "address", "city", "state", "zip", "notes"];

function Mapper({ imp, onStarted }) {
  const [map, setMap] = useState(imp.suggested_mapping || {});
  const start = async () => {
    try { await api.post(`/imports/${imp.id}/start`, { mapping: map }); toast.success("Import queued"); onStarted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 space-y-4 fade-in" data-testid="mapping-panel">
      <h2 className="font-bold">Map columns — {imp.filename}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {FIELDS.map((f) => (
          <label key={f} className="text-xs space-y-1"><span className="text-zinc-600">{f.replace(/_/g, " ")}</span>
            <select className="fld w-full" value={map[f] || ""} onChange={(e) => setMap({ ...map, [f]: e.target.value })} data-testid={`map-${f}`}>
              <option value="">— not mapped —</option>{imp.columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>))}
      </div>
      <div className="overflow-x-auto"><table className="dt" data-testid="import-preview-table">
        <thead><tr>{imp.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{imp.preview.map((r, i) => <tr key={i}>{imp.columns.map((c) => <td key={c} className="text-xs">{String(r[c] ?? "")}</td>)}</tr>)}</tbody>
      </table></div>
      <p className="text-xs text-zinc-500">Preview shows the first 10 rows. Phones are normalized to 11-digit US format; rows without a valid phone or email, or without a name, are rejected with a reason. Existing leads are never reset by duplicates.</p>
      <Button onClick={start} data-testid="start-import-btn">Start import</Button>
    </div>
  );
}

export default function UploadsPage() {
  const [imp, setImp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [list, reload] = useLoad(async () => (await api.get("/imports")).data);
  useEffect(() => {
    const t = setInterval(() => { if ((list || []).some((i) => ["queued", "processing"].includes(i.status))) reload(); }, 3000);
    return () => clearInterval(t);
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const SIZE = 8 * 1024 * 1024;
    const parts = Math.max(1, Math.ceil(file.size / SIZE));
    try {
      const { id } = (await api.post("/imports/init", { filename: file.name, size: file.size, parts })).data;
      let done = 0;
      const send = async (n) => {
        const fd = new FormData(); fd.append("file", file.slice(n * SIZE, (n + 1) * SIZE), `part-${n}`);
        for (let a = 0; ; a++) {
          try { await api.post(`/imports/${id}/parts/${n}`, fd); break; } catch (e) { if (a >= 4) throw e; await new Promise((r) => setTimeout(r, 2000 * (a + 1))); }
        }
        done += 1; setPct(Math.round((done / parts) * 100));
      };
      for (let n = 0; n < parts; n += 4) await Promise.all([n, n + 1, n + 2, n + 3].filter((k) => k < parts).map(send));
      setImp((await api.post(`/imports/${id}/complete`)).data);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false); setPct(null);
  };
  return (
    <div data-testid="uploads-page">
      <PageHeader title="Lead uploads" sub="CSV (any size, uploaded in 8 MB pieces) or Excel (small files). Imports run in the background and resume after restarts. No download links exist.">
        <label className="inline-flex items-center h-9 px-4 rounded-full bg-zinc-950 text-white text-sm font-semibold cursor-pointer hover:bg-[#58a80a] transition-colors">
          {busy ? `Uploading… ${pct ?? 0}%` : "Upload lead file"}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="lead-file-input" />
        </label>
      </PageHeader>
      {imp && imp.status === "uploaded" && <div className="mb-8"><Mapper imp={imp} onStarted={() => { setImp(null); reload(); }} /></div>}
      <PoolWash />
      <RemoveBySource />
      <h2 className="text-lg font-bold mb-3">Import history</h2>
      <div className="panel overflow-x-auto">
        {!list?.length ? <Empty text="No imports yet." /> : (
          <table className="dt" data-testid="imports-table"><thead><tr><th>File</th><th>Uploaded</th><th>Status</th><th className="num">Progress</th><th className="num">Rows</th><th className="num">New</th><th className="num">Duplicates</th><th className="num">Ambiguous</th><th className="num">Rejected</th><th /></tr></thead>
            <tbody>{list.map((i) => (
              <tr key={i.id} data-testid={`import-row-${i.id}`}><td className="font-medium">{i.filename}{i.totals?.sources && <div className="flex flex-wrap gap-1 mt-1" data-testid={`import-sources-${i.id}`}>{Object.entries(i.totals.sources).map(([k, v]) => <span key={k} className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">{k} · {v.toLocaleString()}</span>)}</div>}</td><td className="mono text-xs">{fmtDT(i.uploaded_at)}</td>
                <td><StatusBadge s={i.status} /></td><td className="num">{i.progress}%</td><td className="num">{i.totals?.rows?.toLocaleString() ?? "—"}</td>
                <td className="num">{i.totals?.inserted?.toLocaleString() ?? "—"}</td><td className="num">{i.totals?.duplicates?.toLocaleString() ?? "—"}</td><td className="num">{i.totals?.ambiguous?.toLocaleString() ?? "—"}</td>
                <td className="num">{i.totals?.rejected?.toLocaleString() ?? "—"}</td>
                <td className="text-right space-x-1 whitespace-nowrap">{i.status === "failed" && <Button size="sm" variant="outline" onClick={async () => { try { await api.post(`/imports/${i.id}/resume`); reload(); } catch (e) { toast.error(errMsg(e)); } }} data-testid={`import-resume-${i.id}`}>Resume</Button>}
                  {i.status === "uploaded" ? <Button size="sm" variant="outline" onClick={async () => setImp((await api.get(`/imports/${i.id}`)).data)} data-testid={`import-map-${i.id}`}>Map</Button>
                  : <Button size="sm" variant="outline" onClick={async () => setDetail((await api.get(`/imports/${i.id}`)).data)} data-testid={`import-detail-${i.id}`}>Details</Button>}</td></tr>))}
            </tbody></table>)}
      </div>
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="import-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.filename}</DialogTitle></DialogHeader>
          <div className="text-xs mono">{detail?.totals?.rows?.toLocaleString()} rows · {detail?.totals?.inserted?.toLocaleString()} new · {detail?.totals?.duplicates?.toLocaleString()} duplicates · {detail?.totals?.ambiguous?.toLocaleString()} ambiguous · {detail?.totals?.rejected?.toLocaleString()} rejected</div>
          {detail?.totals?.reason_counts && <div className="text-xs">Rejection reasons: {Object.entries(detail.totals.reason_counts).map(([k, v]) => `${k} ${v.toLocaleString()}`).join(" · ")}</div>}
          {detail?.error && <div className="text-rose-600 text-sm">{detail.error}</div>}
          <h3 className="font-bold text-sm">Rejected rows (first 200 shown)</h3>
          {!detail?.rejections?.length ? <Empty text="No rejected rows." /> : (
            <table className="dt"><thead><tr><th className="num">Row</th><th>Reason</th></tr></thead>
              <tbody>{detail.rejections.map((r) => <tr key={r.row}><td className="num">{r.row}</td><td>{r.reasons.join("; ")}</td></tr>)}</tbody></table>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
